import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { validateLogin } from "../../services/validate-login";
import Header from "../../components/common/Header";
import Footer from "../../components/common/Footer";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import Loader from "../../components/common/Loader";
import { initResponseService, initService } from "../../services/ondcService";

function Form() {
  const [accHolderName, setAccHolderName] = useState("");
  const [accType, setAccType] = useState("saving");
  const [accNo, setAccNo] = useState("");
  const [bankName, setBankName] = useState("");
  const [branchName, setbranchName] = useState("");
  const [state, setState] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [formIsComplete, setFormIsComplete] = useState(false);  
  const [error, setError] = useState(""); 
  const [loading, setLoading] = useState(true);
  const [lendersOffer, setLendersOffer] = useState<any>();
  const navigate = useNavigate();

  const validateIFSC = (value: string) => {
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    if (!ifscRegex.test(value)) {
      setError("Invalid IFSC code (e.g., SBIN0001234)");
    } else {
      setError("");
    }
  };

  useEffect(() => {
    async function getStep() {
      if(Number(localStorage.getItem("step")) >= 9){
        localStorage.removeItem("ekyc_submission_id")
      } else{ navigate("/personal-loan") }
    }
    getStep();
  }, []);

  useEffect(() => {
    async function fetchOffers() {
      try {
        const data = {
          transaction_id: localStorage.getItem("transaction_id"),
          message_id: localStorage.getItem("message_id"),
        };
        const response = await initResponseService(data);
        if (response.success) {
          setLendersOffer(response.message.response[0])
        }
      } catch (err) {
        console.error("Error fetching offers:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchOffers();
  }, []);
  
  const [nameError, setNameError] = useState("");
  const [nError, setNError] = useState(false);

  const validateAccHolderName = (value: string) => {
    if (!value.trim()) {
      setNError(false)
      setNameError("Account Holder Name is required");
      return false;
    }
    if (!/^[A-Za-z ]+$/.test(value)) {
      setNError(false)
      setNameError("Only alphabets and spaces are allowed");
      return false;
    }
    if (value.trim().length < 3) {
      setNError(false)
      setNameError("Name must be at least 3 characters long");
      return false;
    }
    setNError(true)
    setNameError(""); // no error
    return true;
  };

    const [accError, setAccError] = useState("");
    const [aError, setAError] = useState(false);

  const validateAccNo = (value: string) => {
    if (!value.trim()) {
      setAError(false)
      setAccError("Bank Account Number is required");
      return false;
    }
    if (!/^[0-9]+$/.test(value)) {
      setAError(false)
      setAccError("Only numbers are allowed");
      return false;
    }
    if (value.length < 9 || value.length > 18) {
      setAError(false)
      setAccError("Account Number must be between 9 to 18 digits");
      return false;
    }
    setAccError("");
    setAError(true)
    return true;
  };

  const checkForm = () => {
    if(accHolderName.length >= 3 && nError && aError && accNo.length >9 && error == "" && ifscCode !=""){
      setFormIsComplete(true)
    }else{
      setFormIsComplete(false)
    }
  }

  const fetchIFSCDetails = async (ifsc: string) => {
    try {
      const response = await fetch(`https://ifsc.razorpay.com/${ifsc}`);
      if (!response.ok) {
        throw new Error("Invalid IFSC Code");
      }
      const data = await response.json();
      setBankName(data.BANK);
      setbranchName(data.BRANCH);
      setState(data.STATE);
      setError(""); // clear error if valid
    } catch (err: any) {
      console.error("Error fetching IFSC details:", err);
      setError("Invalid IFSC Code");
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toUpperCase();
    setIfscCode(val);
    validateIFSC(val)

    // Trigger API only when 11 characters entered
    if (val.length === 11) {
      fetchIFSCDetails(val);
    } else {
      // setError(""); // clear error while typing
    }
  };

  const onClickEvent = async () => {
    localStorage.setItem("message_id", uuidv4());
    try {
      setLoading(true); // show loader
      var data = {
          "transaction_id": localStorage.getItem("transaction_id"),
          "message_id": localStorage.getItem("message_id"),
          "action": "init2",
          "bpp_id": lendersOffer.bpp_id,
          "bpp_uri": lendersOffer.bpp_uri,
          "provider_id": lendersOffer.provider_id,
          "item_id": lendersOffer.item_id,
          "form_id": lendersOffer.form_id,
          "form_url": lendersOffer.form_url,
          "prev_msg_id": lendersOffer.prev_msg_id,
          "version": lendersOffer.version,
          "payment": lendersOffer.payment,
          "acc_holder_name": accHolderName,
          "acc_type": accType,
          "acc_no": accNo,
          "ifsc_code": ifscCode
      }
      const response = await initService(data);
      if (response.success) {
        localStorage.setItem("step", "10")
        // wait for 5 seconds before navigating
        await new Promise((resolve) => setTimeout(resolve, 5000));
        navigate("/e-mandate");
      }
    } catch (err) {
      console.error("Error fetching offers:", err);
    } finally {
      setLoading(false); // hide loader
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#f5f9ff]">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-[#f5f9ff] px-4">
      {/* Hero Section */}
      <div className="flex flex-col md:flex-row max-w-5xl w-full bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Left side Image (fixed) */}
        <div className="basis-full md:basis-2/5 flex items-center justify-center p-6 bg-gray-50 md:h-[calc(100vh-100px)] md:sticky md:top-0">
          <img
            src="svg/img9.svg"
            alt="Working professional"
            className="w-full max-w-xs md:max-w-none object-contain"
          />
        </div>

        {/* Right side Form (scrollable) */}
        <div className="basis-full md:basis-3/5 flex flex-col p-8 bg-white relative md:h-[calc(100vh-100px)] md:overflow-y-auto">
          {/* Step Number */}
          <div className="absolute top-4 right-4 bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
            Step 10
          </div>
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto">
            <h2 className="text-xl font-semibold mb-6">Bank Details</h2>
            <p className="text-sm text-gray-600 mb-4">
              Enter Bank details for Loan amount Disbursement
            </p>

            {/* Extra Fields */}
            <div className="space-y-6">
              {/* Account Holder Name Field */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Account Holder Name
                </label>
                <input
                  type="text"
                  value={accHolderName}
                  onKeyUp={checkForm}
                  onChange={(e) => {
                    setAccHolderName(e.target.value);
                    validateAccHolderName(e.target.value);
                  }}
                  placeholder="As per Bank Database"
                  className={`w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none ${
                    nameError ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {nameError && <p className="text-red-500 text-xs mt-1">{nameError}</p>}
              </div>

              {/* Account Type */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">Account Type</label>
                <select
                  value={accType}
                  onClick={(e) => checkForm()}
                  onKeyDown={(e) => checkForm()}
                  onChange={(e) => setAccType(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="saving">Saving</option>
                  <option value="current">Current</option>
                </select>
              </div>

              {/* Annual Income Field */}
              <div>
      <label className="block text-sm text-gray-600 mb-1">
        Bank Account Number
      </label>
      <div
        className={`flex items-center border rounded-lg ${
          accError ? "border-red-500" : "border-gray-300"
        }`}
      >
        <input
          type="text"
          value={accNo}
          onKeyUp={checkForm}
          onChange={(e) => {
            const val = e.target.value;
            // Prevent non-digits while typing
            if (/^[0-9]*$/.test(val)) {
              setAccNo(val);
              validateAccNo(val);
            }
          }}
          placeholder="Enter Bank Account Number"
          className="w-full px-4 py-2 rounded-r-lg focus:ring-2 focus:ring-blue-500 outline-none border-0"
        />
      </div>
      {accError && <p className="text-red-500 text-xs mt-1">{accError}</p>}
    </div>

              {/* Bank IFSC Code Field */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Bank IFSC Code
                </label>
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <input
                    type="text"
                    value={ifscCode}
                    onChange={handleChange}
                    onKeyUp={(e) => checkForm()}
                    maxLength={11}
                    placeholder="Enter Bank IFSC Code"
                    className="w-full px-4 py-2 rounded-r-lg outline-none border-0"
                  />
                </div>
                {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
              </div>
                            {/* Annual Income Field */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Bank Name
                </label>
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <input
                    type="text"
                    value={bankName}
                    readOnly
                    className="w-full px-4 py-2 rounded-r-lg focus:ring-2 focus:ring-blue-500 outline-none border-0"
                  />
                </div>
              </div>
                            {/* Annual Income Field */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Branch Name
                </label>
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <input
                    type="text"
                    value={branchName}
                    readOnly
                    className="w-full px-4 py-2 rounded-r-lg focus:ring-2 focus:ring-blue-500 outline-none border-0"
                  />
                </div>
              </div>
                            {/* Annual Income Field */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  State
                </label>
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <input
                    type="text"
                    value={state}
                    onChange={checkForm}
                    readOnly
                    className="w-full px-4 py-2 rounded-r-lg focus:ring-2 focus:ring-blue-500 outline-none border-0"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Next Buttons in a row */}
          <div className="mt-6 flex gap-4">
            <button
              className={`flex-1 px-8 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition
                ${formIsComplete
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "bg-gray-400 cursor-not-allowed"}`}
              onClick={onClickEvent}
              disabled={!formIsComplete}
            >
              Next <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Footer Stats */}
      <Footer />
    </div>
  );
}

export default function Step9BankDetails() {
  const navigate = useNavigate();

  useEffect(() => {
    validateLogin(navigate);
  }, [navigate]);

  return (
    <div className="flex flex-col h-screen overflow-y-auto">
      <div className="flex-shrink-0">
        <Header title="" />
      </div>

      {/* Main Content scrollable */}
      <main className="flex-1 overflow-y-auto">
          <Form />
      </main>
    </div>
  );
}
